import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/app/Shared";
import { Check, Crown, Sparkles, Layers, Loader2 } from "lucide-react";
import { toast } from "sonner";

const PLAN_META = {
  basic:      { icon: Layers,   color: "from-zinc-50 to-white",  border: "border-zinc-200",  accent: "text-zinc-700" },
  pro:        { icon: Sparkles, color: "from-blue-50 to-white",  border: "border-blue-200",  accent: "text-blue-700" },
  enterprise: { icon: Crown,    color: "from-amber-50 to-white", border: "border-amber-200", accent: "text-amber-700" },
};

const PLAN_FEATURES = {
  basic: ["1 locatie", "Onbeperkte producten", "E-mail support"],
  pro: ["Tot 3 locaties", "Onbeperkte producten", "Prioriteit e-mail support", "Multi-location dashboard"],
  enterprise: ["Onbeperkte locaties", "Onbeperkte producten", "Dedicated support", "Audit logs export", "Custom rapportages"],
};

export default function Billing({ embedded }) {
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [org, setOrg] = useState(null);
  const [busy, setBusy] = useState(null);
  const [params, setParams] = useSearchParams();

  const load = async () => {
    try {
      const [p, o] = await Promise.all([
        api.get("/billing/plans"),
        api.get("/me/organization"),
      ]);
      setPlans(p.data);
      setOrg(o.data);
    } catch { toast.error("Laden mislukt"); }
  };
  useEffect(() => { load(); }, []);

  // Handle return from Stripe Checkout
  useEffect(() => {
    const sessionId = params.get("session_id");
    const cancelled = params.get("cancelled");
    if (cancelled) {
      toast.info("Upgrade geannuleerd");
      setParams({});
      return;
    }
    if (!sessionId) return;

    let attempts = 0;
    let cancelledFlag = false;
    const poll = async () => {
      if (cancelledFlag) return;
      attempts += 1;
      try {
        const { data } = await api.get(`/billing/status/${sessionId}`);
        if (data.payment_status === "paid") {
          toast.success(`Welkom op het ${data.plan} plan!`);
          setParams({});
          load();
          return;
        }
        if (data.status === "expired") {
          toast.error("Betaalsessie verlopen");
          setParams({});
          return;
        }
        if (attempts >= 8) {
          toast.info("Status nog niet bevestigd — ververs deze pagina later.");
          setParams({});
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        if (attempts < 5) setTimeout(poll, 2000);
      }
    };
    poll();
    return () => { cancelledFlag = true; };
  }, [params, setParams]);

  const upgrade = async (planId) => {
    if (planId === "basic" && currentPlan === "basic") return;
    setBusy(planId);
    try {
      const origin = window.location.origin;
      const { data } = await api.post("/billing/checkout", { plan: planId, origin_url: origin });
      if (data.url) window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upgrade mislukt");
      setBusy(null);
    }
  };

  const currentPlan = org?.plan || "basic";

  return (
    <div className={embedded ? "" : "p-4 sm:p-6 lg:p-8 max-w-[1200px] mx-auto"} data-testid="billing-page">
      {!embedded && (
        <PageHeader
          overline="Abonnement"
          title="Kies je plan"
          description={org ? `Je organisatie "${org.name}" gebruikt nu ${org.usage?.restaurants ?? 0} van ${org.plan_limit === -1 ? "∞" : org.plan_limit} restaurants.` : ""}
        />
      )}
      {embedded && org && (
        <div className="mb-6 text-sm text-zinc-500">
          Je organisatie <b className="text-[#1B2D5C]">{org.name}</b> gebruikt nu {org.usage?.restaurants ?? 0} van {org.plan_limit === -1 ? "∞" : org.plan_limit} restaurants.
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        {plans.map((p) => {
          const meta = PLAN_META[p.id] || PLAN_META.basic;
          const Icon = meta.icon;
          const isCurrent = currentPlan === p.id;
          return (
            <div
              key={p.id}
              className={`relative card-flat overflow-hidden bg-gradient-to-b ${meta.color} border ${isCurrent ? "ring-2 ring-[#5BC044] " : ""}${meta.border}`}
              data-testid={`plan-card-${p.id}`}
            >
              {isCurrent && (
                <div className="absolute top-3 right-3 bg-[#5BC044] text-white text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded">Huidig plan</div>
              )}
              <div className="p-6">
                <div className={`w-10 h-10 rounded-md border ${meta.border} ${meta.accent} flex items-center justify-center mb-4 bg-white`}>
                  <Icon size={18} strokeWidth={1.8} />
                </div>
                <div className="overline mb-1">{p.name}</div>
                <div className="text-3xl font-bold tracking-tight text-[#1B2D5C] mb-1">
                  €{p.price_eur} <span className="text-sm font-medium text-zinc-500">/maand</span>
                </div>
                <div className="text-sm text-zinc-500 mb-5">{p.limit >= 999 ? "Onbeperkte locaties" : `Tot ${p.limit} locatie${p.limit === 1 ? "" : "s"}`}</div>

                <ul className="space-y-2 mb-6">
                  {(PLAN_FEATURES[p.id] || []).map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      <Check size={14} className="text-[#5BC044] mt-0.5 shrink-0" strokeWidth={2.5} />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => upgrade(p.id)}
                  disabled={isCurrent || busy === p.id}
                  className={`w-full h-11 rounded-md text-sm font-semibold inline-flex items-center justify-center gap-2 transition-colors ${
                    isCurrent ? "bg-zinc-100 text-zinc-500 cursor-default"
                    : "bg-[#1B2D5C] hover:bg-[#142244] text-white"
                  }`}
                  data-testid={`upgrade-${p.id}`}
                >
                  {busy === p.id ? <><Loader2 size={14} className="animate-spin" /> Bezig…</>
                    : isCurrent ? "Huidig plan"
                    : `Kies ${p.name}`}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <div className="card-flat p-5 text-sm text-zinc-600 leading-relaxed">
        <b className="text-[#1B2D5C]">Veilige betaling</b> · Betalingen worden veilig afgehandeld door Stripe.
        Je abonnement gaat direct in na succesvolle betaling. <span className="text-zinc-400">Dit is een testomgeving — gebruik testkaart <code className="px-1.5 py-0.5 bg-zinc-100 rounded font-mono text-xs">4242 4242 4242 4242</code></span>
      </div>
    </div>
  );
}
